import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import { updateSEO } from '../../../utils/seo';

// ===== 専門資格ページ 新デザイン全面最新化（型正典: src/pages/qualifications/business-manner/page.tsx） =====
// 設計note（共通骨格・9ページ共通の設計言語）:
//   紺#1a2942 / 真鍮金#a38e65 / brass2 #8f7a56 / gold-signboard #d7b779 / cream #f8f6f0 / line #e2ddd2。
//   見出し=jba-display（Shippori Mincho B1）+ 金差し色span。eyebrow=Playfair uppercase。BTN-08 2状態。
//   共通セクション骨格（全異型・装飾ディバイダー/素hr/❦/紋章 不使用）:
//     Hero(全幅ビルボード) → I.資格概要(リード3段落+細罫データ行+特徴3枚) → II.カリキュラム(番号2カラム+画像)
//     → III.こんな方におすすめ(紺パネル+2カラム点検リスト) → IV.教材(番号カード+画像) → V.認定試験(紺罫定義リスト)
//     → VI.料金とお申込み(大字serif受講料+含まれるもの) → VII.FAQ(アコーディオン) → VIII.関連資格+締めCTA。
//   実文出典（創作禁止）: 現行working tree実文の逐語（scratchpad/qual-content.md §9 butler-style-uniform）。
//     数値・受講料(38,000円)・章立て・FAQ(20問)は旧実装から改変せず継承。
// butler-style-uniform = hero型A(左寄せ全幅) / 紺パネル主役=III（Standard）。

export default function ButlerStyleUniformPage() {
  useEffect(() => {
    updateSEO({
      title: 'Butler Style & Uniform資格 | 執事の装いと美学を学ぶ | 全日本執事協会',
      description: '全日本執事協会認定のButler Style & Uniform資格。執事服の歴史、フォーマルウェアの構成、身だしなみの哲学、TPO別の装いまで、執事が体現する品格ある装いの美学を学ぶ専門資格。料金38,000円。',
      keywords: 'Butler Style,Uniform,執事服,装い,美学,フォーマルウェア,全日本執事協会',
      canonical: 'https://butler-association.jp/qualifications/butler-style-uniform'
    });
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '資格制度', href: '/qualifications' },
    { label: 'Butler Style & Uniform' }
  ];

  // BTN-08（canon と同一文法・2状態）
  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const features = [
    { icon: 'ri-time-line', title: '歴史的理解', desc: '執事服の変遷と文化的背景。中世の使用人服からモーニングコート・燕尾服・タキシードへと続く装いの歴史を学びます。' },
    { icon: 'ri-briefcase-line', title: '実用的知識', desc: 'ビジネス正装にも応用可能。フォーマルウェアの原則は、ビジネスシーンや冠婚葬祭での正装にもそのまま活かせます。' },
    { icon: 'ri-star-line', title: '美学の習得', desc: '品格を表現する装いの哲学。清潔感・フィット感・色の選択を通じて、服装で品格を表現する技術を身につけます。' },
  ];

  const curriculum = [
    { no: '01', title: '執事服の歴史', desc: '中世の使用人の服装、19世紀のモーニングコートの誕生、テールコート（燕尾服）の登場、タキシードと執事の関係、現代の執事ユニフォームへの変遷を学びます。' },
    { no: '02', title: 'フォーマルウェアの基礎', desc: 'モーニングコートの特徴と着用場面、テールコート（燕尾服）の構造、タキシード（ディナージャケット）の歴史、スリーピーススーツの役割、それぞれのTPOを習得します。' },
    { no: '03', title: '執事服の構成要素', desc: 'ジャケット・ベスト・トラウザーズの選び方、シャツとカラーの種類、ネクタイとボウタイの結び方、カフスボタンの意味、ポケットチーフの挿し方を学びます。' },
    { no: '04', title: '靴と小物', desc: 'フォーマルシューズの選び方と手入れ、手袋（白手袋の意味と使い方）、懐中時計の役割、ハンカチーフの扱い、その他のアクセサリーを習得します。' },
    { no: '05', title: '女性執事の装い', desc: '女性執事の歴史的背景、現代の女性執事ユニフォーム、フォーマルスーツの選び方、アクセサリーの選択基準、品格ある装いの原則を学びます。' },
    { no: '06', title: '身だしなみの哲学', desc: '清潔感の維持と重要性、サイズ感とフィット感の追求、色の選択と組み合わせ、執事としての品格の表現方法を習得します。' },
    { no: '07', title: 'TPO別の装い', desc: '朝の服装（モーニング）、昼の服装（デイウェア）、夜の正装（イブニングウェア）、季節による違いと配慮を学びます。' },
    { no: '08', title: 'お手入れとメンテナンス', desc: 'スーツのケアと保管方法、シャツのアイロンがけ技術、靴の磨き方と手入れ、長期保管の注意点を習得します。' },
    { no: '09', title: '映画・ドラマの執事ファッション', desc: '有名な執事キャラクターの服装分析、時代考証と創作の違い、執事服のアイコンとなった作品、フィクションから学ぶスタイリングを学びます。' },
    { no: '10', title: '総合演習', desc: 'コーディネート実践、TPO判断の練習、確認テストで学習内容を統合します。' },
  ];

  const recommend = [
    '執事のファッションに興味がある方', 'フォーマルウェアの知識を深めたい方', 'ビジネス正装を学びたい方',
    'ファッション業界で働く方', 'スタイリストを目指す方', '冠婚葬祭での装いに自信を持ちたい方',
    '映画・ドラマの衣装に興味がある方', '執事文化を深く理解したい方',
  ];

  const materials = [
    { no: '1', title: 'テキスト教材（約80ページ）', desc: '執事の装いと美学を詳しく解説した専用テキスト。' },
    { no: '2', title: 'オンライン動画レッスン', desc: 'ネクタイの結び方や靴の磨き方など実践的な動画解説。視聴期間1年間。' },
    { no: '3', title: 'フォーマルウェア図解集', desc: '各種フォーマルウェアの構造と名称を視覚的に理解できる参考資料。' },
    { no: '4', title: '認定試験問題・解答用紙', desc: '筆記試験とコーディネート課題を含む試験問題。' },
  ];

  const exam = [
    { label: '試験形式', value: '筆記試験（記述式・選択式）+ コーディネート課題' },
    { label: '合格基準', value: '80点以上' },
    { label: '試験期限', value: '申込から1年以内' },
    { label: '受験資格', value: '特になし' },
    { label: '受験場所', value: '自宅（通信試験）' },
    { label: '再試験', value: '再試験料8,000円で何度でも可' },
  ];

  const included = [
    'テキスト教材（約80ページ）', 'オンライン動画レッスン（1年間視聴可能）', 'フォーマルウェア図解集',
    '認定試験問題・解答用紙', '試験添削サービス', '合格時の認定証・認定カード', '送料',
  ];

  const related = [
    { href: '/qualifications/business-manner', name: 'Business Manner Butler資格', ja: 'ビジネスマナーの専門知識を学ぶ', icon: 'ri-briefcase-line' },
    { href: '/qualifications/afternoon-tea', name: 'Afternoon Tea Butler資格', ja: 'アフタヌーンティーの文化とマナーを学ぶ', icon: 'ri-cup-line' },
    { href: '/qualifications', name: '専門資格一覧を見る', ja: 'すべての専門資格をチェック', icon: 'ri-list-check' },
  ];

  const faqs = [
    { q: 'フォーマルウェア初心者でも取得できますか？', a: 'はい、基礎から学べる内容です。スーツの知識がなくても段階的に理解できます。' },
    { q: '女性でも受講できますか？', a: 'もちろんです。第5章で女性執事の装いを詳しく学びます。' },
    { q: 'ビジネススーツの選び方も学べますか？', a: 'はい、フォーマルウェアの原則はビジネススーツにも応用できます。' },
    { q: '実際に執事服を購入する必要がありますか？', a: 'いいえ、購入は不要です。知識の習得が目的です。' },
    { q: 'モーニングコートとタキシードの違いは？', a: '第2章で各フォーマルウェアの違いと着用場面を詳しく学びます。' },
    { q: '靴の磨き方も学べますか？', a: 'はい、第8章で靴の手入れ方法を実践的に学びます。動画レッスンもあります。' },
    { q: 'ネクタイの結び方も学べますか？', a: 'はい、第3章でネクタイとボウタイの結び方を学びます。' },
    { q: '冠婚葬祭での装いに役立ちますか？', a: 'はい、第7章でTPO別の装いを学ぶため、冠婚葬祭にも応用できます。' },
    { q: '映画の執事ファッションについても学べますか？', a: 'はい、第9章で有名な執事キャラクターの服装を分析します。' },
    { q: 'スーツのサイズ選びのコツは？', a: '第6章でサイズ感とフィット感の重要性について詳しく学びます。' },
    { q: '白手袋の意味とは？', a: '第4章で手袋の歴史的意味と使い方を学びます。清潔さと品格の象徴です。' },
    { q: '女性執事の歴史も学べますか？', a: 'はい、第5章で女性執事の歴史的背景から現代の装いまで学びます。' },
    { q: 'アイロンのかけ方も学べますか？', a: 'はい、第8章でシャツのアイロンがけ技術を詳しく学びます。' },
    { q: 'カフスボタンの選び方は？', a: '第3章でカフスボタンの意味と選び方を学びます。' },
    { q: '季節による装いの違いは？', a: '第7章で季節ごとの配慮事項を詳しく学びます。' },
    { q: '執事服の色の選び方は？', a: '第6章で色の選択と組み合わせの原則を学びます。' },
    { q: 'スーツの保管方法も学べますか？', a: 'はい、第8章で長期保管の注意点とケア方法を学びます。' },
    { q: 'ポケットチーフの挿し方は？', a: '第3章でポケットチーフの挿し方とスタイリングを学びます。' },
    { q: 'コーディネート課題とは何ですか？', a: '与えられたシーンに適した装いを提案する記述課題です。' },
    { q: 'ファッション業界で働いていますが役立ちますか？', a: 'はい、フォーマルウェアの歴史的背景や構造の理解が深まります。' },
  ];

  return (
    <Layout breadcrumbs={breadcrumbs}>
      {/* ===== Hero — 全幅ビルボード型A（画像+紺グラデ+左寄せ）。由来: about/overview ヒーロー ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[540px] lg:min-h-[620px] flex items-center">
        <img
          src="/images/generated/jba-fs-154-qualifications_butler-style-uniform.jpg"
          alt="執事の装いと美学。フォーマルウェアを身にまとった執事の品格ある佇まい"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.93)_0%,rgba(16,20,26,0.74)_46%,rgba(16,20,26,0.34)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.3em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Specialist Certification</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(30px,4vw,50px)] leading-[1.3] tracking-[0.04em] mb-3">Butler Style &amp; Uniform</h1>
            <p className="font-['Noto_Serif_JP',serif] text-[#d7b779] text-base sm:text-lg tracking-[0.12em] mb-6">バトラー・スタイル＆ユニフォーム</p>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95] max-w-[600px]">
              Butler Style &amp; Uniform資格は、執事の装いが持つ歴史と美学を体系的に学ぶ専門資格です。中世から現代まで続く執事服の伝統、モーニングコート・テールコート・タキシードの違い、フォーマルウェアの構成要素、TPO別の装い方まで、品格ある装いの哲学を習得します。
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 lg:px-10 py-14 sm:py-20 space-y-16 sm:space-y-24">

          {/* ===== I. 資格概要（リード3段落+細罫データ行+特徴3枚）。由来: about/overview S1 ===== */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. About This Certification</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              執事の装いに宿る、<span className="text-[#a38e65]">品格の美学</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
              <p>
                Butler Style &amp; Uniform資格は、執事の装いが持つ歴史と美学を体系的に学ぶ専門資格です。中世から現代まで続く執事服の伝統、モーニングコート・テールコート・タキシードの違い、フォーマルウェアの構成要素、TPO別の装い方まで、品格ある装いの哲学を習得します。
              </p>
              <p>
                執事ファッションの知識は、ビジネスシーンや冠婚葬祭での正装にも応用できる実用的な内容。服装を通じて品格を表現する技術を学びます。
              </p>
              <p>
                執事服の歴史から構成要素、靴と小物、女性執事の装い、身だしなみの哲学、TPO別の装い、お手入れとメンテナンス、映画・ドラマのスタイリングまで。装いのあらゆる側面を体系立てて学び、服装を通じて品格を表現する技術を身につけます。
              </p>
            </div>

            {/* 細罫データ行 */}
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">受講料（税込）</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">38,000<span className="text-sm text-[#8f7a56] ml-1">円</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">学習期間目安</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">1–3<span className="text-sm text-[#8f7a56] ml-1">ヶ月</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">カリキュラム</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">全10<span className="text-sm text-[#8f7a56] ml-1">章</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">受験・資格</dt>
                <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">自宅受験・永久</dd>
              </div>
            </dl>

            {/* 特徴3枚（アイコン編集カード） */}
            <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 mt-12">
              {features.map((f) => (
                <div key={f.title} className="border-t-2 border-[#a38e65] bg-[#f8f6f0] px-6 py-7">
                  <i className={`${f.icon} text-3xl text-[#a38e65]`}></i>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mt-4 mb-2.5">{f.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-start gap-3 border border-[#a38e65]/40 bg-[#f8f6f0] px-5 py-4">
              <i className="ri-information-line text-[#a38e65] text-xl mt-0.5"></i>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.85]">
                本資格は知識習得を目的とした認定制度であり、就職や収入を保証するものではありません。全日本執事協会が認定する民間資格です。
              </p>
            </div>
          </section>

          {/* ===== II. カリキュラム（番号2カラム編集ブロック+画像）。由来: about/overview S4 ===== */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. Curriculum</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              学ぶ内容 — <span className="text-[#a38e65]">全10章</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-8">
              執事の装いと美学を、10の分野で体系的に学びます。各章は実践的な内容で構成され、知識だけでなく着こなしの技術も習得できます。
            </p>
            <img
              src="/images/generated/jba-fs-155-qualifications_butler-style-uniform-v5.jpg"
              alt="執事の装いを学ぶカリキュラム教材。フォーマルウェアの構造や着こなしを解説するテキストと資料"
              className="w-full aspect-[21/9] h-auto object-cover object-center border border-[#a38e65]/30 mb-10"
            />
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
              {curriculum.map((c) => (
                <div key={c.no} className="flex gap-5 border-t border-[#e2ddd2] pt-6">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none shrink-0 pt-1">{c.no}</span>
                  <div>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2 leading-[1.5]">{c.title}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ===== III. こんな方におすすめ（紺パネル+2カラム点検リスト）= Standard主役セクション。由来: about/overview S3 翻案 ===== */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. Who It's For</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              こんな方におすすめです
            </h2>
            <div className="bg-[#1a2942] px-6 sm:px-10 lg:px-14 py-10 sm:py-12">
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
                {recommend.map((r) => (
                  <div key={r} className="flex items-start gap-3 border-b border-white/10 pb-4">
                    <i className="ri-check-line text-[#d7b779] text-xl mt-0.5 shrink-0"></i>
                    <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#ded8cc] leading-[1.8]">{r}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== IV. お届けする教材（番号カード+画像）。由来: about/overview S2 の罫線カード文法 ===== */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. Course Materials</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              お届けする教材
            </h2>
            <div className="grid lg:grid-cols-[1fr_360px] gap-8 lg:gap-12 items-start">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                {materials.map((m) => (
                  <div key={m.no} className="flex gap-4 border-t border-[#e2ddd2] pt-5">
                    <span className="font-['Playfair_Display',serif] text-[#a38e65] text-xl w-8 h-8 shrink-0 flex items-center justify-center border border-[#a38e65]/40 rounded-full">{m.no}</span>
                    <div>
                      <h3 className="jba-display font-medium text-[#1a2942] text-base mb-1.5 leading-[1.5]">{m.title}</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <img
                src="/images/generated/jba-fs-156-qualifications_butler-style-uniform-v5.jpg"
                alt="執事の装い教材一式。テキスト・動画レッスン・フォーマルウェア図解集がそろった学習セット"
                className="w-full h-56 lg:h-[300px] object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* ===== V. 認定試験について（紺罫定義リスト）。由来: about/overview S2 の border-t-2 定義リスト文法 ===== */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">V. Examination</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              認定試験について
            </h2>
            <dl className="border-t-2 border-[#1a2942] max-w-3xl">
              {exam.map((row) => (
                <div key={row.label} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                  <dt className="sm:w-[140px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{row.label}</dt>
                  <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{row.value}</dd>
                </div>
              ))}
            </dl>
            <p className="font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.9] mt-5 max-w-3xl">
              試験は自宅で行う通信試験です。教材が届いたら、ご自身のペースで学習を進め、準備ができた段階で試験問題に取り組みます。解答用紙を返送いただくと、当協会が添削・採点し、80点以上で合格。認定証と認定カードを発行します。
            </p>
          </section>

          {/* ===== VI. 料金とお申込み（大字serif受講料+含まれるもの）。受講料は clamp 大字serif強調 ===== */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VI. Enrollment</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              料金とお申込み
            </h2>
            <div className="bg-[#f8f6f0] border border-[#a38e65]/30 px-6 sm:px-10 lg:px-12 py-10">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 border-b border-[#e2ddd2] pb-8 mb-8">
                <div>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.08em] mb-2">受講料（税込・送料込）</p>
                  <div className="jba-display text-[#1a2942] leading-none">
                    <span className="font-['Playfair_Display',serif] text-[clamp(44px,7vw,64px)] font-semibold">38,000</span>
                    <span className="text-2xl sm:text-3xl ml-1">円</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-6 text-center sm:text-right">
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">学習期間目安</p>
                    <p className="jba-display text-[#1a2942] text-base">1〜3ヶ月</p>
                  </div>
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">試験期限</p>
                    <p className="jba-display text-[#1a2942] text-base">1年以内</p>
                  </div>
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">資格有効期限</p>
                    <p className="jba-display text-[#1a2942] text-base">永久</p>
                  </div>
                </div>
              </div>
              <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-5">料金に含まれるもの</h3>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {included.map((it) => (
                  <div key={it} className="flex items-start gap-3">
                    <i className="ri-check-line text-[#a38e65] text-lg mt-0.5 shrink-0"></i>
                    <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.8]">{it}</span>
                  </div>
                ))}
              </div>
              <div className="mt-9">
                <a href="/contact" className={`${BTN08_ON_LIGHT} w-full sm:w-auto`}>Butler Style &amp; Uniform資格に申し込む</a>
              </div>
            </div>
            <ul className="mt-6 space-y-2">
              {['本資格は全日本執事協会が認定する民間資格です', '資格取得により就職・転職・収入を保証するものではありません', '知識習得と自己研鑽を目的としています'].map((n) => (
                <li key={n} className="flex items-start gap-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.85]">
                  <i className="ri-information-line text-[#a38e65] text-base mt-0.5 shrink-0"></i>
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* ===== VII. よくある質問（アコーディオン） ===== */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VII. FAQ</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくある質問
            </h2>
            <div className="border-t border-[#e2ddd2]">
              {faqs.map((f) => (
                <details key={f.q} className="group border-b border-[#e2ddd2]">
                  <summary className="flex items-center justify-between gap-4 cursor-pointer py-5 list-none">
                    <span className="jba-display font-medium text-[#1a2942] text-base sm:text-[17px] leading-[1.6]">{f.q}</span>
                    <i className="ri-add-line text-[#a38e65] text-xl shrink-0 transition-transform group-open:rotate-45"></i>
                  </summary>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95] pb-5 pr-8">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* ===== VIII. 関連資格 + 締めCTA（紺パネル）。由来: about/overview S6 ===== */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VIII. Related Certifications</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              あわせて学びたい専門資格
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-8">
              他の専門資格も組み合わせて取得することで、より幅広い専門性を証明できます。
            </p>
            <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 mb-12">
              {related.map((r) => (
                <a key={r.href} href={r.href} className="group block border border-[#e2ddd2] hover:border-[#a38e65] bg-white px-6 py-7 transition-colors">
                  <i className={`${r.icon} text-2xl text-[#a38e65]`}></i>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mt-4 mb-1.5 leading-[1.5]">{r.name}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85] mb-4">{r.ja}</p>
                  <span className="inline-flex items-center gap-2 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] group-hover:text-[#a38e65] transition-colors">
                    詳しく見る<i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                  </span>
                </a>
              ))}
            </div>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                Butler Style &amp; Uniform資格について、ご不明な点やお申込みのご相談はお気軽にお問い合わせください。専門資格の全体像は資格制度の一覧からもご覧いただけます。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ・申込</a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>資格制度の一覧を見る</a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
